'use client';
import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { containerService, type ContainerModel } from '@/lib/services/containerService';
import { createClient } from '@/lib/supabase/client';

type CatalogCategory = 'customer' | 'distributor' | 'wholesale' | 'merchandise';

const VALID_CATEGORIES: CatalogCategory[] = ['customer', 'distributor', 'wholesale', 'merchandise'];

interface ProductItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  compare_at_price: number | null;
  image_url: string | null;
  in_stock: boolean;
  stock_quantity: number;
  sku: string | null;
  tags: string[];
  active: boolean;
}

const CATALOG_META: Record<CatalogCategory, { title: string; highlight: string; description: string; badge: string }> = {
  customer: {
    title: 'Customer',
    highlight: 'Catalog',
    description: 'Browse our turn-key container solutions designed for end-customers. Ready-to-operate units with professional equipment included.',
    badge: 'Customer Pricing',
  },
  distributor: {
    title: 'Distributor',
    highlight: 'Catalog',
    description: 'Exclusive distributor pricing and container models. Partner with us to offer premium container solutions in your market.',
    badge: 'Distributor Pricing',
  },
  wholesale: {
    title: 'Wholesale',
    highlight: 'Catalog',
    description: 'Bulk pricing for wholesale buyers. Volume discounts available on orders of 5+ cases.',
    badge: 'Wholesale Pricing',
  },
  merchandise: {
    title: 'Merchandise',
    highlight: 'Store',
    description: 'Official branded merchandise from Take a Zip. Show your support with premium quality gear.',
    badge: 'Merchandise',
  },
};

function getCategoryFromUrl(): CatalogCategory | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const cat = params.get('category');
  if (cat && VALID_CATEGORIES.includes(cat as CatalogCategory)) {
    return cat as CatalogCategory;
  }
  return null;
}

const AllModelsSection = () => {
  const [models, setModels] = useState<ContainerModel[]>([]);
  const [wholesaleProducts, setWholesaleProducts] = useState<ProductItem[]>([]);
  const [merchandiseProducts, setMerchandiseProducts] = useState<ProductItem[]>([]);
  const [distributorProducts, setDistributorProducts] = useState<ProductItem[]>([]);
  const [customerProducts, setCustomerProducts] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<CatalogCategory | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setCategory(getCategoryFromUrl());
  }, []);

  useEffect(() => {
    const supabase = createClient();
    let cancelled = false;

    const fetchAll = async () => {
      try {
        const [containerData, wholesaleData, merchData, distData, custData] = await Promise.all([
          containerService.getAll(),
          supabase
            .from('products')
            .select('*')
            .eq('category', 'wholesale')
            .eq('active', true)
            .order('sort_order', { ascending: true }),
          supabase
            .from('products')
            .select('*')
            .eq('category', 'merchandise')
            .eq('active', true)
            .order('sort_order', { ascending: true }),
          supabase
            .from('products')
            .select('*')
            .eq('category', 'distributor')
            .eq('active', true)
            .order('sort_order', { ascending: true }),
          supabase
            .from('products')
            .select('*')
            .eq('category', 'customer')
            .eq('active', true)
            .order('sort_order', { ascending: true }),
        ]);
        if (!cancelled) {
          setModels(containerData);
          setWholesaleProducts(wholesaleData.data || []);
          setMerchandiseProducts(merchData.data || []);
          setDistributorProducts(distData.data || []);
          setCustomerProducts(custData.data || []);
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to load catalog data:', error);
        if (!cancelled) setLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event) => {
      if (!cancelled) fetchAll();
    });

    fetchAll();

    return () => { cancelled = true; subscription.unsubscribe(); };
  }, []);

  useEffect(() => {
    if (loading) return;
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [loading, category]);

  const handleCategoryChange = useCallback((cat: CatalogCategory | null) => {
    setCategory(cat);
    const url = cat ? `/models-pricing?category=${cat}` : '/models-pricing';
    window.history.replaceState({}, '', url);
  }, []);

  const meta = mounted && category && CATALOG_META[category] ? CATALOG_META[category] : null;
  const showProducts = category === 'wholesale' || category === 'merchandise' || category === 'distributor' || category === 'customer';
  const activeProducts = category === 'wholesale' ? wholesaleProducts : category === 'distributor' ? distributorProducts : category === 'customer' ? customerProducts : merchandiseProducts;

  if (loading) {
    return (
      <section className="py-32 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center">
            <Icon name="ArrowPathIcon" size={40} className="animate-spin text-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">Loading catalog...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {meta && (
          <div className="reveal mb-8">
            <button
              onClick={() => handleCategoryChange(null)}
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-medium mb-4"
            >
              <Icon name="ArrowLeftIcon" size={16} />
              View All Models
            </button>
          </div>
        )}

        <div className="reveal text-center mb-16">
          {meta ? (
            <>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-semibold uppercase px-4 py-2 rounded-full mb-6">
                <Icon name="TagIcon" size={16} />
                {meta.badge}
              </div>
              <h1 className="text-5xl md:text-6xl font-bold font-montserrat text-foreground mb-6">
                {meta.title} <span className="text-primary">{meta.highlight}</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {meta.description}
              </p>
            </>
          ) : (
            <>
              <h1 className="text-5xl md:text-6xl font-bold font-montserrat text-foreground mb-6">
                Container Models & <span className="text-primary">Pricing</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Explore our complete range of container models. All prices include standard features, professional equipment, and turn-key delivery.
              </p>
            </>
          )}
        </div>

        {mounted && (
          <div className="reveal flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => handleCategoryChange(null)}
              className={`px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${
                !category
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'
              }`}
            >
              All Models
            </button>
            {(Object.keys(CATALOG_META) as CatalogCategory[]).map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${
                  category === cat
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'
                }`}
              >
                {CATALOG_META[cat].title}
              </button>
            ))}
          </div>
        )}

        {showProducts ? (
          /* Product grid for Wholesale, Merchandise & Distributor */
          activeProducts.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="ShoppingBagIcon" size={64} className="text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">No Products Available</h2>
              <p className="text-muted-foreground">Check back soon for new {category} products.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {activeProducts.map((product) => (
                <div
                  key={product.id}
                  className="reveal bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group"
                >
                  <div className="aspect-square bg-gray-100 relative overflow-hidden">
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon name="PhotoIcon" size={48} className="text-gray-300" />
                      </div>
                    )}
                    {product.compare_at_price && product.compare_at_price > product.price && (
                      <div className={`absolute top-3 left-3 text-white text-xs font-bold px-2 py-1 rounded-full ${
                        category === 'wholesale' ? 'bg-purple-600' : category === 'distributor' ? 'bg-green-600' : 'bg-red-500'
                      }`}>
                        SAVE ${(product.compare_at_price - product.price).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </div>
                    )}
                    {category === 'wholesale' && (
                      <div className="absolute top-3 right-3 bg-purple-100 text-purple-700 text-xs font-bold px-2 py-1 rounded-full">
                        WHOLESALE
                      </div>
                    )}
                    {category === 'distributor' && (
                      <div className="absolute top-3 right-3 bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full">
                        DISTRIBUTOR
                      </div>
                    )}
                    {!product.in_stock && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="bg-white text-foreground font-bold px-4 py-2 rounded-lg">Out of Stock</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4 sm:p-5">
                    <h3 className="font-bold text-foreground text-lg mb-1">{product.name}</h3>
                    {product.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{product.description}</p>
                    )}
                    <div className="flex items-center gap-2">
                      <span className={`text-xl font-bold ${category === 'wholesale' ? 'text-purple-600' : category === 'distributor' ? 'text-green-600' : 'text-primary'}`}>
                        ${product.price.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </span>
                      {product.compare_at_price && product.compare_at_price > product.price && (
                        <span className="text-sm text-muted-foreground line-through">
                          ${product.compare_at_price.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                      )}
                      {category === 'wholesale' && (
                        <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded font-medium">per case</span>
                      )}
                      {category === 'distributor' && (
                        <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded font-medium">per pallet</span>
                      )}
                    </div>
                    {product.in_stock && (
                      <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                        <Icon name="CheckCircleIcon" size={14} />
                        In Stock ({product.stock_quantity} {category === 'wholesale' ? 'cases' : category === 'distributor' ? 'pallets' : 'available'})
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          /* Container models grid for All Models */
          <div className="grid md:grid-cols-2 gap-8">
            {models?.map((model, index) => (
              <div
                key={model.id}
                className="reveal container-card group relative"
                style={{ animationDelay: `${0.1 * (index + 1)}s` }}
              >
                {model.featured && (
                  <div className="absolute top-4 right-4 z-10 bg-primary text-primary-foreground text-xs font-bold uppercase px-3 py-1.5 rounded-full">
                    Most Popular
                  </div>
                )}
                <div className="absolute top-4 left-4 z-10 bg-success text-success-foreground text-xs font-bold uppercase px-3 py-1.5 rounded-full">
                  Save ${model.savings.toLocaleString()}
                </div>

                <div className="relative h-80 overflow-hidden rounded-t-2xl">
                  <AppImage
                    src={model.images[0]?.url || '/assets/images/no_image.png'}
                    alt={model.images[0]?.alt || model.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                <div className="p-8">
                  <div className="inline-flex items-center gap-2 bg-gray-100 text-foreground text-xs font-semibold uppercase px-3 py-1.5 rounded-full mb-4">
                    <Icon name="CubeIcon" size={14} />
                    {model.code}
                  </div>
                  <h3 className="text-3xl font-bold font-montserrat text-foreground mb-2">
                    {model.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-6 flex items-center gap-2">
                    <Icon name="RectangleStackIcon" size={16} />
                    {model.dimensions}
                  </p>
                  <div className="space-y-2 mb-6">
                    {model.features?.map((feature, idx) => (
                      <div
                        key={`${model.id}_feature_${idx}`}
                        className="flex items-center gap-2 text-sm text-muted-foreground"
                      >
                        <Icon name="CheckCircleIcon" size={16} className="text-primary" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-end gap-3 mb-6">
                    <div className="text-4xl font-bold text-primary">
                      ${model.price.toLocaleString()}
                    </div>
                    <div className="text-lg text-muted-foreground line-through mb-1">
                      ${model.originalPrice.toLocaleString()}
                    </div>
                  </div>
                  <Link
                    href={`/get-a-quote?model=${model.code}`}
                    className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold rounded-full px-6 py-3 hover:bg-primary/90 transition-all"
                  >
                    Request Quote
                    <Icon name="ArrowRightIcon" size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AllModelsSection;
